import { useEffect, useMemo, useRef, useState } from 'react';
import { Star, Zap, X } from 'lucide-react';
import api from '../../services/api';
import { normalizeMiniGameNaveAssets, resolveMiniGameAssetSrc } from './miniGameNaveUtils';

const CANVAS_WIDTH = 360;
const CANVAS_HEIGHT = 640;
const PLAYER_SIZE = 34;
const ENEMY_SIZE = 28;
const BULLET_WIDTH = 4;
const BULLET_HEIGHT = 12;
const PLAYER_SPEED = 260;
const PLAYER_FIRE_COOLDOWN = 180;
const ENEMY_BULLET_SPEED = 220;
const ENEMY_SPAWN_BASE_MS = 950;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function rectsOverlap(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function createStars() {
  return Array.from({ length: 80 }).map(() => ({
    x: Math.random() * CANVAS_WIDTH,
    y: Math.random() * CANVAS_HEIGHT,
    speed: 20 + Math.random() * 70,
    size: 1 + Math.random() * 1.8,
    alpha: 0.3 + Math.random() * 0.7
  }));
}

function createInitialState(assets) {
  return {
    phase: 'idle',
    score: 0,
    lives: 3,
    wave: 1,
    enemiesDestroyed: 0,
    combo: 0,
    comboTimer: 0,
    startedAt: performance.now(),
    lastFrameAt: 0,
    lastEnemySpawnAt: 0,
    enemiesSpawnedInWave: 0,
    waveTarget: 8,
    backgroundScroll: 0,
    player: {
      x: CANVAS_WIDTH / 2 - PLAYER_SIZE / 2,
      y: CANVAS_HEIGHT - 82,
      w: PLAYER_SIZE,
      h: PLAYER_SIZE + 6,
      cooldownMs: 0,
      invincibleMs: 0
    },
    bullets: [],
    enemyBullets: [],
    enemies: [],
    explosions: [],
    stars: createStars(),
    assets: normalizeMiniGameNaveAssets(assets)
  };
}

function createEnemy(state, assets) {
  const enemyImageCount = Math.max(1, assets.enemy_images.length);
  const typeIndex = Math.floor(Math.random() * enemyImageCount);
  const lane = Math.floor(Math.random() * 5);
  const laneWidth = CANVAS_WIDTH / 5;
  const x = Math.max(10, Math.min(CANVAS_WIDTH - ENEMY_SIZE - 10, lane * laneWidth + Math.random() * (laneWidth - ENEMY_SIZE)));
  const waveFactor = Math.max(1, state.wave);
  const hpBase = 1 + Math.floor(waveFactor / 4);
  const hp = typeIndex === 0 ? 1 : typeIndex === 1 ? 2 : hpBase + 1;
  const speed = 40 + waveFactor * 6 + typeIndex * 10 + Math.random() * 26;
  const drift = (Math.random() - 0.5) * (20 + waveFactor * 2);
  const shootChance = Math.min(0.0025 + waveFactor * 0.00045 + typeIndex * 0.0004, 0.012);
  return {
    x,
    y: -ENEMY_SIZE - Math.random() * 120,
    w: ENEMY_SIZE + typeIndex * 4,
    h: ENEMY_SIZE + typeIndex * 3,
    hp,
    maxHp: hp,
    speed,
    drift,
    shootChance,
    spriteIndex: typeIndex,
    phaseOffset: Math.random() * Math.PI * 2
  };
}

function drawFallbackShip(ctx, x, y, w, h, color, flip = false) {
  ctx.save();
  ctx.translate(x + w / 2, y + h / 2);
  if (flip) ctx.scale(-1, 1);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, -h / 2);
  ctx.lineTo(w / 2, h / 3);
  ctx.lineTo(0, h / 2);
  ctx.lineTo(-w / 2, h / 3);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.18)';
  ctx.fillRect(-w * 0.12, -h * 0.15, w * 0.24, h * 0.55);
  ctx.restore();
}

function drawImageOrFallback(ctx, image, x, y, w, h, fallbackColor, flip = false) {
  if (image && image.complete && image.naturalWidth > 0) {
    try {
      ctx.save();
      if (flip) {
        ctx.translate(x + w, y);
        ctx.scale(-1, 1);
        ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, w, h);
      } else {
        ctx.drawImage(image, x, y, w, h);
      }
      ctx.restore();
      return;
    } catch (_error) {
      // Fallback below
    }
  }

  drawFallbackShip(ctx, x, y, w, h, fallbackColor, flip);
}

function createExplosion(x, y, color = '#f97316') {
  return {
    x,
    y,
    life: 0.35,
    maxLife: 0.35,
    color
  };
}

async function loadRanking(limit = 10) {
  const response = await api.get('/robots/my-city-rpg-mini-game-nave/ranking', { params: { limit } });
  return {
    ranking: Array.isArray(response.data?.ranking) ? response.data.ranking : [],
    playerBest: response.data?.playerBest || null
  };
}

export default function RpgMiniGameNaveModal({
  isOpen,
  onClose,
  cityId,
  cityName,
  userName,
  interaction = null
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const isRunningRef = useRef(false);
  const keysRef = useRef({ left: false, right: false, up: false, down: false, fire: false });
  const stateRef = useRef(null);
  const savedScoreRef = useRef(false);
  const imageRef = useRef({});
  const [hud, setHud] = useState({
    phase: 'idle',
    score: 0,
    lives: 3,
    wave: 1,
    enemiesDestroyed: 0,
    combo: 0,
    message: 'Pulsa E para jugar.'
  });
  const [rankingState, setRankingState] = useState({
    loading: false,
    ranking: [],
    playerBest: null
  });
  const [saveState, setSaveState] = useState({
    saving: false,
    error: '',
    saved: false
  });

  const assets = useMemo(() => normalizeMiniGameNaveAssets(interaction?.game_assets), [interaction?.game_assets]);
  const playerImageSrc = useMemo(() => resolveMiniGameAssetSrc(assets.player_image), [assets.player_image]);
  const enemyImageSrcs = useMemo(() => assets.enemy_images.map(resolveMiniGameAssetSrc), [assets.enemy_images]);
  const backgroundImageSrc = useMemo(() => resolveMiniGameAssetSrc(assets.background_image), [assets.background_image]);
  const bulletImageSrc = useMemo(() => resolveMiniGameAssetSrc(assets.bullet_image), [assets.bullet_image]);
  const explosionImageSrc = useMemo(() => resolveMiniGameAssetSrc(assets.explosion_image), [assets.explosion_image]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const keys = {
      left: false,
      right: false,
      up: false,
      down: false,
      fire: false
    };
    keysRef.current = keys;

    const onKeyDown = (event) => {
      const code = String(event.code || event.key || '');
      if (code === 'ArrowLeft' || code === 'KeyA') keys.left = true;
      if (code === 'ArrowRight' || code === 'KeyD') keys.right = true;
      if (code === 'ArrowUp' || code === 'KeyW') keys.up = true;
      if (code === 'ArrowDown' || code === 'KeyS') keys.down = true;
      if (code === 'Space' || code === 'KeyJ' || code === 'KeyK') keys.fire = true;
      if (code === 'KeyE') {
        const current = stateRef.current;
        if (current && current.phase !== 'playing') {
          event.preventDefault();
          startGame();
        }
      }
      if (code === 'Escape') onClose();
    };

    const onKeyUp = (event) => {
      const code = String(event.code || event.key || '');
      if (code === 'ArrowLeft' || code === 'KeyA') keys.left = false;
      if (code === 'ArrowRight' || code === 'KeyD') keys.right = false;
      if (code === 'ArrowUp' || code === 'KeyW') keys.up = false;
      if (code === 'ArrowDown' || code === 'KeyS') keys.down = false;
      if (code === 'Space' || code === 'KeyJ' || code === 'KeyK') keys.fire = false;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const imageMap = {};
    const loadOne = (key, src) => new Promise((resolve) => {
      if (!src) {
        imageMap[key] = null;
        resolve();
        return;
      }
      const image = new window.Image();
      image.onload = () => {
        imageMap[key] = image;
        resolve();
      };
      image.onerror = () => {
        imageMap[key] = null;
        resolve();
      };
      image.src = src;
    });

    Promise.all([
      loadOne('player', playerImageSrc),
      loadOne('background', backgroundImageSrc),
      loadOne('bullet', bulletImageSrc),
      loadOne('explosion', explosionImageSrc),
      ...enemyImageSrcs.map((src, index) => loadOne(`enemy-${index}`, src))
    ]).then(() => {
      if (!cancelled) {
        imageRef.current = imageMap;
      }
    });

    return () => {
      cancelled = true;
    };
  }, [backgroundImageSrc, bulletImageSrc, enemyImageSrcs, explosionImageSrc, isOpen, playerImageSrc]);

  const syncHud = (next) => {
    setHud((current) => {
      if (
        current.phase === next.phase
        && current.score === next.score
        && current.lives === next.lives
        && current.wave === next.wave
        && current.enemiesDestroyed === next.enemiesDestroyed
        && current.combo === next.combo
        && current.message === next.message
      ) {
        return current;
      }
      return next;
    });
  };

  const resetGame = () => {
    stateRef.current = createInitialState(assets);
    savedScoreRef.current = false;
    setSaveState({ saving: false, error: '', saved: false });
    syncHud({
      phase: 'idle',
      score: 0,
      lives: 3,
      wave: 1,
      enemiesDestroyed: 0,
      combo: 0,
      message: 'Pulsa E para jugar.'
    });
  };

  const startGame = () => {
    resetGame();
    const current = stateRef.current;
    if (!current) return;
    current.phase = 'playing';
    current.startedAt = performance.now();
    current.lastFrameAt = 0;
    startWave(current);
    syncHud({
      phase: 'playing',
      score: current.score,
      lives: current.lives,
      wave: current.wave,
      enemiesDestroyed: current.enemiesDestroyed,
      combo: current.combo,
      message: 'Destruye enemigos y sobrevive.'
    });
  };

  const fetchRanking = async () => {
    setRankingState((current) => ({ ...current, loading: true }));
    try {
      const data = await loadRanking(10);
      setRankingState({ loading: false, ranking: data.ranking, playerBest: data.playerBest });
    } catch (_error) {
      setRankingState({ loading: false, ranking: [], playerBest: null });
    }
  };

  const persistScore = async (state) => {
    if (savedScoreRef.current) return;
    savedScoreRef.current = true;
    setSaveState({ saving: true, error: '', saved: false });
    try {
      const response = await api.post('/robots/my-city-rpg-mini-game-nave/score', {
        score: state.score,
        waves_survived: Math.max(0, state.wave - 1),
        enemies_destroyed: state.enemiesDestroyed,
        duration_ms: Math.max(0, Math.round(performance.now() - state.startedAt)),
        metadata: {
          cityId,
          cityName,
          interactionType: interaction?.type || 'mini-juego-nave'
        }
      });
      const savedScore = response.data?.score || null;
      setSaveState({ saving: false, error: '', saved: true });
      await fetchRanking();
      if (savedScore) {
        setRankingState((current) => {
          const currentRanking = Array.isArray(current.ranking) ? current.ranking : [];
          const mergedRanking = [
            savedScore,
            ...currentRanking.filter((entry) => Number(entry.id) !== Number(savedScore.id))
          ].slice(0, 10);
          const currentBest = current.playerBest;
          const nextBest = !currentBest || Number(savedScore.score) >= Number(currentBest.score)
            ? savedScore
            : currentBest;
          return {
            ...current,
            ranking: mergedRanking,
            playerBest: nextBest
          };
        });
      }
    } catch (error) {
      setSaveState({
        saving: false,
        error: error.response?.data?.error || 'No se pudo guardar la puntuación',
        saved: false
      });
    }
  };

  const endGame = (state, message = 'Nave destruida') => {
    if (state.phase === 'gameover') return;
    state.phase = 'gameover';
    syncHud({
      phase: 'gameover',
      score: state.score,
      lives: 0,
      wave: state.wave,
      enemiesDestroyed: state.enemiesDestroyed,
      combo: state.combo,
      message
    });
    void persistScore(state);
  };

  const startWave = (state) => {
    state.enemiesSpawnedInWave = 0;
    state.waveTarget = 8 + state.wave * 4;
    state.lastEnemySpawnAt = 0;
  };

  const shootPlayer = (state) => {
    const player = state.player;
    if (player.cooldownMs > 0) return;
    state.bullets.push({
      x: player.x + player.w / 2 - BULLET_WIDTH / 2,
      y: player.y - 12,
      w: BULLET_WIDTH,
      h: BULLET_HEIGHT,
      speed: 560
    });
    player.cooldownMs = PLAYER_FIRE_COOLDOWN;
  };

  const spawnWaveEnemy = (state) => {
    if (state.enemiesSpawnedInWave >= state.waveTarget) return;
    state.enemies.push(createEnemy(state, assets));
    state.enemiesSpawnedInWave += 1;
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    resetGame();
    fetchRanking().catch(() => {});
    isRunningRef.current = true;

    const draw = (ctx, current) => {
      const bgImage = imageRef.current.background;
      const bulletImage = imageRef.current.bullet;
      const playerImage = imageRef.current.player;
      const explosionImage = imageRef.current.explosion;

      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      const background = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
      background.addColorStop(0, '#0a3a78');
      background.addColorStop(1, '#05111e');
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      if (bgImage && bgImage.complete && bgImage.naturalWidth > 0) {
        const ratio = Math.max(CANVAS_WIDTH / bgImage.naturalWidth, CANVAS_HEIGHT / bgImage.naturalHeight);
        const w = bgImage.naturalWidth * ratio;
        const h = bgImage.naturalHeight * ratio;
        ctx.globalAlpha = 0.55;
        ctx.drawImage(bgImage, 0, (current.backgroundScroll % h) - h, w, h);
        ctx.drawImage(bgImage, 0, (current.backgroundScroll % h), w, h);
        ctx.globalAlpha = 1;
      }

      current.stars.forEach((star) => {
        ctx.fillStyle = `rgba(255,255,255,${star.alpha})`;
        ctx.fillRect(star.x, star.y, star.size, star.size);
      });

      current.explosions.forEach((explosion) => {
        const progress = 1 - explosion.life / explosion.maxLife;
        const radius = 8 + progress * 24;
        if (explosionImage && explosionImage.complete && explosionImage.naturalWidth > 0) {
          ctx.globalAlpha = Math.max(0, explosion.life / explosion.maxLife);
          ctx.drawImage(explosionImage, explosion.x - 16, explosion.y - 16, 32, 32);
          ctx.globalAlpha = 1;
        } else {
          ctx.beginPath();
          ctx.arc(explosion.x, explosion.y, radius, 0, Math.PI * 2);
          ctx.fillStyle = explosion.color;
          ctx.globalAlpha = Math.max(0, explosion.life / explosion.maxLife);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      });

      current.bullets.forEach((bullet) => {
        if (bulletImage && bulletImage.complete && bulletImage.naturalWidth > 0) {
          ctx.drawImage(bulletImage, bullet.x - 2, bullet.y - 4, 8, 16);
        } else {
          ctx.fillStyle = '#facc15';
          ctx.fillRect(bullet.x, bullet.y, bullet.w, bullet.h);
        }
      });

      current.enemyBullets.forEach((bullet) => {
        ctx.fillStyle = '#fb7185';
        ctx.fillRect(bullet.x, bullet.y, bullet.w, bullet.h);
      });

      current.enemies.forEach((enemy) => {
        const enemySrc = enemyImageSrcs[enemy.spriteIndex % Math.max(1, enemyImageSrcs.length)];
        const enemyImage = imageRef.current[`enemy-${enemy.spriteIndex}`] || null;
        const alpha = enemy.hp / enemy.maxHp;
        ctx.globalAlpha = 0.95;
        drawImageOrFallback(
          ctx,
          enemyImage,
          enemy.x,
          enemy.y,
          enemy.w,
          enemy.h,
          enemy.spriteIndex % 2 === 0 ? '#84cc16' : '#38bdf8',
          false
        );
        ctx.globalAlpha = alpha;
        ctx.fillStyle = 'rgba(255,255,255,0.22)';
        ctx.fillRect(enemy.x, enemy.y - 4, enemy.w * alpha, 3);
        ctx.globalAlpha = 1;
        if (enemySrc) {
          ctx.strokeStyle = 'rgba(255,255,255,0.02)';
          ctx.strokeRect(enemy.x, enemy.y, enemy.w, enemy.h);
        }
      });

      const player = current.player;
      const playerAlpha = player.invincibleMs > 0 ? 0.7 : 1;
      ctx.globalAlpha = playerAlpha;
      drawImageOrFallback(ctx, playerImage, player.x, player.y, player.w, player.h, '#e2e8f0', false);
      ctx.globalAlpha = 1;

      ctx.fillStyle = 'rgba(255,255,255,0.25)';
      ctx.fillRect(12, 12, 188, 72);
      ctx.fillStyle = '#e0f2fe';
      ctx.font = 'bold 14px system-ui, sans-serif';
      ctx.fillText(`Score: ${current.score}`, 22, 34);
      ctx.fillText(`Vidas: ${current.lives}`, 22, 52);
      ctx.fillText(`Oleada: ${current.wave}`, 22, 70);
      ctx.fillText(`Enemigos: ${current.enemiesDestroyed}`, 112, 34);
      ctx.fillText(`Combo: x${current.combo}`, 112, 52);

        if (current.phase !== 'playing') {
          ctx.fillStyle = 'rgba(2, 6, 23, 0.68)';
          ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
          ctx.fillStyle = '#f8fafc';
          ctx.textAlign = 'center';
          ctx.font = '900 28px system-ui, sans-serif';
          ctx.fillText(current.phase === 'gameover' ? 'GAME OVER' : 'MISION LISTA', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 34);
          ctx.font = '700 16px system-ui, sans-serif';
          if (current.phase === 'gameover') {
            ctx.fillText(`Score final: ${current.score}`, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
          }
          ctx.fillText('Pulsa E para jugar', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 26);
          ctx.textAlign = 'start';
        }
    };

    const tick = (timestamp) => {
      if (!isRunningRef.current) return;
      const current = stateRef.current;
      if (!current) {
        rafRef.current = window.requestAnimationFrame(tick);
        return;
      }
      const deltaMs = current.lastFrameAt ? Math.min(32, timestamp - current.lastFrameAt) : 16;
      const dt = deltaMs / 1000;
      current.lastFrameAt = timestamp;

      if (current.phase === 'playing') {
        const { left, right, up, down, fire } = keysRef.current;
        const player = current.player;
        let dx = 0;
        let dy = 0;
        if (left) dx -= 1;
        if (right) dx += 1;
        if (up) dy -= 1;
        if (down) dy += 1;
        const norm = Math.hypot(dx, dy) || 1;
        player.x = clamp(player.x + (dx / norm) * PLAYER_SPEED * dt, 4, CANVAS_WIDTH - player.w - 4);
        player.y = clamp(player.y + (dy / norm) * PLAYER_SPEED * dt, 40, CANVAS_HEIGHT - player.h - 12);
        player.cooldownMs = Math.max(0, player.cooldownMs - deltaMs);
        player.invincibleMs = Math.max(0, player.invincibleMs - deltaMs);

        if (fire) shootPlayer(current);

        current.backgroundScroll += 36 * dt;
        current.stars.forEach((star) => {
          star.y += star.speed * dt;
          if (star.y > CANVAS_HEIGHT) {
            star.y = -2;
            star.x = Math.random() * CANVAS_WIDTH;
          }
        });

        current.comboTimer = Math.max(0, current.comboTimer - deltaMs);
        if (current.comboTimer <= 0) current.combo = 0;

        current.bullets = current.bullets
          .map((bullet) => ({ ...bullet, y: bullet.y - bullet.speed * dt }))
          .filter((bullet) => bullet.y + bullet.h > -20);

        current.enemyBullets = current.enemyBullets
          .map((bullet) => ({ ...bullet, y: bullet.y + bullet.speed * dt }))
          .filter((bullet) => bullet.y < CANVAS_HEIGHT + 20);

        current.enemies = current.enemies
          .map((enemy) => ({
            ...enemy,
            y: enemy.y + enemy.speed * dt,
            x: clamp(enemy.x + Math.sin((timestamp / 240) + enemy.phaseOffset) * enemy.drift * dt, 4, CANVAS_WIDTH - enemy.w - 4)
          }))
          .filter((enemy) => enemy.y < CANVAS_HEIGHT + 40);

        current.explosions = current.explosions
          .map((explosion) => ({ ...explosion, life: explosion.life - dt }))
          .filter((explosion) => explosion.life > 0);

        if (current.enemiesSpawnedInWave < current.waveTarget) {
          const spawnInterval = Math.max(260, ENEMY_SPAWN_BASE_MS - current.wave * 45);
          if (!current.lastEnemySpawnAt || (timestamp - current.lastEnemySpawnAt) > spawnInterval) {
            spawnWaveEnemy(current);
            current.lastEnemySpawnAt = timestamp;
          }
        }

        current.enemies.forEach((enemy) => {
          if (Math.random() < enemy.shootChance) {
            current.enemyBullets.push({
              x: enemy.x + enemy.w / 2 - 2,
              y: enemy.y + enemy.h,
              w: 4,
              h: 10,
              speed: ENEMY_BULLET_SPEED + current.wave * 6
            });
          }
        });

        current.bullets = current.bullets.filter((bullet) => {
          const hitEnemy = current.enemies.find((enemy) => rectsOverlap(bullet, enemy));
          if (!hitEnemy) return true;
          hitEnemy.hp -= 1;
          current.explosions.push(createExplosion(hitEnemy.x + hitEnemy.w / 2, hitEnemy.y + hitEnemy.h / 2, '#f97316'));
          if (hitEnemy.hp <= 0) {
            current.enemiesDestroyed += 1;
            current.score += 100 + current.wave * 15 + current.combo * 12;
            current.combo += 1;
            current.comboTimer = 1600;
            current.explosions.push(createExplosion(hitEnemy.x + hitEnemy.w / 2, hitEnemy.y + hitEnemy.h / 2, '#facc15'));
            current.enemies = current.enemies.filter((enemy) => enemy !== hitEnemy);
          } else {
            current.score += 10;
          }
          return false;
        });

        current.enemyBullets = current.enemyBullets.filter((bullet) => {
          if (!rectsOverlap(bullet, player)) return true;
          if (player.invincibleMs <= 0) {
            player.invincibleMs = 1200;
            current.lives -= 1;
            current.combo = 0;
            current.comboTimer = 0;
            current.explosions.push(createExplosion(player.x + player.w / 2, player.y + player.h / 2, '#fb7185'));
            if (current.lives <= 0) {
              endGame(current, 'La nave fue destruida');
            }
          }
          return false;
        });

        current.enemies.forEach((enemy) => {
          if (enemy.y + enemy.h >= CANVAS_HEIGHT - 18) {
            if (player.invincibleMs <= 0) {
              current.lives -= 1;
              current.combo = 0;
              current.comboTimer = 0;
              player.invincibleMs = 1000;
              current.explosions.push(createExplosion(enemy.x + enemy.w / 2, enemy.y + enemy.h / 2, '#ef4444'));
              current.enemies = current.enemies.filter((item) => item !== enemy);
              if (current.lives <= 0) {
                endGame(current, 'La nave fue destruida');
              }
            }
          }
        });

        if (current.enemiesSpawnedInWave >= current.waveTarget && current.enemies.length === 0) {
          current.wave += 1;
          current.score += 250 + current.wave * 20;
          startWave(current);
        }

        syncHud({
          phase: current.phase,
          score: current.score,
          lives: current.lives,
          wave: current.wave,
          enemiesDestroyed: current.enemiesDestroyed,
          combo: current.combo,
          message: current.lives <= 1 ? 'Cuidado, nave muy dañada.' : 'Destruye enemigos y sobrevive.'
        });
      }

      const canvas = canvasRef.current;
      const ctx = canvas ? canvas.getContext('2d') : null;
      if (ctx) {
        draw(ctx, stateRef.current);
      }

      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      isRunningRef.current = false;
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, [assets, enemyImageSrcs, isOpen, playerImageSrc, backgroundImageSrc, bulletImageSrc, explosionImageSrc, cityId, cityName, interaction?.type]);

  useEffect(() => {
    if (!isOpen) return;
    void fetchRanking();
  }, [isOpen]);

  if (!isOpen) return null;

  const ranking = rankingState.ranking || [];
  const playerBest = rankingState.playerBest || null;
  const playerName = String(userName || 'Jugador').trim() || 'Jugador';
  const isSummaryPhase = hud.phase === 'gameover';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 px-4 py-6 backdrop-blur-md">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative z-10 w-full max-w-6xl overflow-hidden rounded-3xl border border-cyan-500/30 bg-[#07101f] shadow-[0_0_60px_rgba(0,0,0,0.45)]">
        <div className="flex items-center justify-between gap-4 border-b border-cyan-500/15 px-5 py-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.35em] text-cyan-300/80">Mini-juego nave</p>
            <h3 className="mt-1 text-xl font-bold text-cyan-100">{interaction?.label || 'Asalto aéreo'} - {cityName || 'Cúpula'}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-700 bg-black/50 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
            aria-label="Cerrar minijuego"
          >
            <X size={16} />
          </button>
        </div>

        <div className={isSummaryPhase ? 'grid gap-4 p-4 xl:grid-cols-[minmax(0,1fr)_360px]' : 'grid gap-4 p-4'}>
          <section className="space-y-3">
            <div className="flex justify-center overflow-hidden rounded-3xl border border-slate-800 bg-[#030712] shadow-inner">
              <canvas
                ref={canvasRef}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                className="block h-[78vh] w-auto max-h-[820px] max-w-full object-contain"
              />
            </div>
            <div className="rounded-3xl border border-cyan-500/15 bg-[#0a1324] px-4 py-3 text-sm text-slate-300 xl:hidden">
              <div className="flex items-center justify-between gap-3">
                <span className="font-bold text-cyan-200">
                  {isSummaryPhase ? 'Fase 2: resumen' : 'Fase 1: combate'}
                </span>
                <span className="text-slate-400">
                  Pulsa <span className="font-bold text-white">E</span> para {isSummaryPhase ? 'volver a jugar' : 'empezar'}
                </span>
              </div>
            </div>
          </section>

          {isSummaryPhase ? (
            <aside className="space-y-4">
              <section className="rounded-3xl border border-slate-800 bg-[#0a1324] p-4">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-lg font-bold text-cyan-300">Resumen</h4>
                  <div className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-xs font-bold text-cyan-100">
                    {playerName}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-200">
                  <div className="rounded-2xl border border-slate-800 bg-black/30 p-3">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Score</p>
                    <p className="mt-1 text-2xl font-black text-white">{hud.score}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-800 bg-black/30 p-3">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Vidas</p>
                    <p className="mt-1 text-2xl font-black text-white">{hud.lives}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-800 bg-black/30 p-3">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Oleada</p>
                    <p className="mt-1 text-2xl font-black text-white">{hud.wave}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-800 bg-black/30 p-3">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Combo</p>
                    <p className="mt-1 text-2xl font-black text-white">x{hud.combo}</p>
                  </div>
                </div>
                <div className="mt-3 rounded-2xl border border-slate-800 bg-black/25 p-3 text-sm text-slate-300">
                  {hud.message}
                </div>
                <div className="mt-3 flex items-center gap-2 text-sm">
                  {saveState.saving && <span className="text-amber-300">Guardando puntuación...</span>}
                  {saveState.saved && <span className="text-emerald-300">Puntuación guardada</span>}
                  {saveState.error && <span className="text-rose-300">{saveState.error}</span>}
                </div>
              </section>

              <section className="rounded-3xl border border-slate-800 bg-[#0a1324] p-4">
                <h4 className="flex items-center gap-2 text-lg font-bold text-cyan-300">
                  <Star size={18} /> Ranking
                </h4>
                <p className="mt-2 text-xs uppercase tracking-[0.25em] text-slate-500">
                  Top pilotos de la Cupula
                </p>
                <div className="mt-3 space-y-2">
                  {rankingState.loading ? (
                    <div className="text-sm text-slate-400">Cargando ranking...</div>
                  ) : ranking.length === 0 ? (
                    <div className="text-sm text-slate-400">Aún no hay puntuaciones.</div>
                  ) : ranking.map((entry, index) => (
                    <div
                      key={entry.id}
                      className={`rounded-2xl border px-3 py-2 text-sm ${entry.username === playerName ? 'border-cyan-400/30 bg-cyan-500/10 text-cyan-50' : 'border-slate-800 bg-black/25 text-slate-200'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold">{index + 1}. {entry.username}</span>
                        <span className="text-amber-200 font-black">{entry.score}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        <span>Oleada {entry.waves_survived}</span>
                        <span>{entry.enemies_destroyed} naves</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-3xl border border-slate-800 bg-[#0a1324] p-4 text-sm text-slate-300">
                <div className="flex items-center gap-2 text-cyan-300 font-bold">
                  <Zap size={16} /> Tu mejor marca
                </div>
                {playerBest ? (
                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-200">
                    <div className="rounded-2xl border border-slate-800 bg-black/30 p-3">
                      <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Score</p>
                      <p className="mt-1 text-2xl font-black text-white">{playerBest.score}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-black/30 p-3">
                      <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Oleada</p>
                      <p className="mt-1 text-2xl font-black text-white">{playerBest.waves_survived}</p>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-slate-400">Aún no tienes marca registrada.</div>
                )}
                <ul className="mt-3 space-y-2">
                  <li>Pulsa <span className="font-bold text-white">E</span> para volver a jugar.</li>
                  <li>Usa el movimiento para limpiar oleadas y subir al ranking.</li>
                </ul>
              </section>
            </aside>
          ) : null}
        </div>
      </div>
    </div>
  );
}
